import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { ownerAPI } from '../utils/api';
import './FamilyMembers.css';

const FamilyMembers = ({ members = [], onUpdate }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name || form.name.length < 2) e.name = 'Name required (min 2 chars)';
    if (!form.phone || !/^\+[1-9]\d{1,14}$/.test(form.phone)) {
      e.phone = 'Use E.164 format: +919876543210';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleAdd = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await ownerAPI.addFamilyMember(form);
      toast.success(`${form.name} added!`);
      setForm({ name: '', phone: '' });
      setShowForm(false);
      setErrors({});
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (memberId, name) => {
    if (!window.confirm(`Remove ${name} from your family members?`)) return;
    try {
      await ownerAPI.removeFamilyMember(memberId);
      toast.success(`${name} removed`);
      onUpdate();
    } catch {
      toast.error('Failed to remove member');
    }
  };

  const handleToggle = async (memberId) => {
    try {
      await ownerAPI.toggleFamilyMember(memberId);
      onUpdate();
    } catch {
      toast.error('Failed to update member');
    }
  };

  return (
    <div className="fm-root animate-fade-in">
      <div className="fm-header">
        <div>
          <p className="fm-desc">
            Family members will be called when your doorbell rings.
            Their phone numbers are never visible to visitors.
          </p>
        </div>
        {members.length < 10 && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowForm(p => !p)}
          >
            {showForm ? '✕ Cancel' : '+ Add Member'}
          </button>
        )}
      </div>

      {/* Add form */}
      {showForm && (
        <div className="fm-form card animate-slide-up">
          <h4 className="fm-form-title">Add Family Member</h4>
          <div className="input-group">
            <label className="input-label">Name</label>
            <input
              className={`input ${errors.name ? 'input--error' : ''}`}
              placeholder="e.g. Mom"
              value={form.name}
              onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
              autoFocus
            />
            {errors.name && <span className="input-error">{errors.name}</span>}
          </div>
          <div className="input-group">
            <label className="input-label">Phone (E.164 format)</label>
            <input
              className={`input ${errors.phone ? 'input--error' : ''}`}
              placeholder="+919876543210"
              value={form.phone}
              onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
              type="tel"
            />
            {errors.phone && <span className="input-error">{errors.phone}</span>}
          </div>
          <p className="fm-privacy-note">
            🔒 This number is stored securely and never shown to visitors.
          </p>
          <button
            className="btn btn-primary btn-block"
            onClick={handleAdd}
            disabled={loading}
          >
            {loading ? <><div className="spinner" /> Adding…</> : 'Add Family Member'}
          </button>
        </div>
      )}

      {/* Members list */}
      {members.length === 0 && !showForm ? (
        <div className="fm-empty">
          <span>👨‍👩‍👧‍👦</span>
          <h4>No family members yet</h4>
          <p>Add family members to ring them when visitors arrive.</p>
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
            Add first member
          </button>
        </div>
      ) : (
        <div className="fm-list">
          {members.map((member, idx) => (
            <div key={member.id} className={`fm-member ${!member.isActive ? 'fm-member--inactive' : ''}`}>
              <div className="fm-member-avatar">
                {member.name[0].toUpperCase()}
              </div>
              <div className="fm-member-info">
                <span className="fm-member-name">{member.name}</span>
                <span className="fm-member-meta">
                  {member.isActive ? '✓ Will be called' : '✕ Disabled'} ·
                  Added {new Date(member.addedAt).toLocaleDateString('en-IN')}
                </span>
              </div>
              <div className="fm-member-actions">
                <button
                  className={`btn btn-sm ${member.isActive ? 'btn-ghost' : 'btn-secondary'}`}
                  onClick={() => handleToggle(member.id)}
                  title={member.isActive ? 'Disable' : 'Enable'}
                >
                  {member.isActive ? '⏸' : '▶'}
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleRemove(member.id, member.name)}
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
          <p className="fm-count-note">
            {members.length}/10 family members
          </p>
        </div>
      )}
    </div>
  );
};

export default FamilyMembers;
